import { PaletteMode } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { vendorColor } from "@vendor/utils/constants";

type ButtonColor = "default" | "black" | "white";
const applePayBtnStyle: ButtonColor | undefined = "white";
const googlePayBtnStyle: ButtonColor | undefined = "white";

export const colors = {
  primary: "#fff",
  primaryDark: "#fff",
  secondary: "#FFFF00",

  error: "#EB1718",
  errorLight: "#fed5d6",
  errorDark: "#5e040a",
  success: "#FFFF00",
  successLight: "#67671c",
  successDark: "#ffff99",
  text: "#fff",
  textSecondary: "#838385",
  textDisabled: "#939597",
  background: "#1a1a1c",
  lighterBackground: "#252528",
  activeSelected: "#737373",
  activeDisabled: "#bebebe",
  activeDisabledBackground: "#737373",
  paper: "#303033",
  border: "#3b3b3e",
  borderLight: "#666669",
  borderMedium: "#515153",
  borderDark: "#161618",
  vendorColor,
  utilityGray: "#737373",
  warning: "#33312f",
  warningDark: "#f49639",
  warningBorder: "#4c4946",
  warningText: "#ccc4bc",
  highlightColor: "#FFFF00",
  switchTrackColor: "#FFFF00",
  applePayBtnStyle,
  googlePayBtnStyle,
};

export const fonts = {
  titlesFont: "'Open Sans', Helvetica, Arial, sans-serif",
  textFont: "'Open Sans', Helvetica, Arial, sans-serif",
};

export const vendorTheme = createTheme({
  typography: {
    fontFamily: fonts.textFont,
    body1: {
      fontSize: "1.6rem",
      color: colors.text,
    },
    h1: {
      fontFamily: fonts.titlesFont,
      fontSize: "1.8rem",
      fontWeight: 600,
    },
    h2: {
      fontFamily: fonts.titlesFont,
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    h5: {
      fontFamily: fonts.titlesFont,
      fontSize: "1.5rem",
      fontWeight: "600",
    },
    button: {
      fontFamily: fonts.titlesFont,
    },
  },
  shape: {
    borderRadius: 6,
  },
  palette: {
    mode: "dark" as PaletteMode,
    primary: {
      main: colors.primary,
      dark: colors.primaryDark,
    },
    secondary: {
      main: colors.secondary,
    },
    error: {
      main: colors.error,
      light: colors.errorLight,
      dark: colors.errorDark,
    },
    success: {
      main: colors.success,
      light: colors.successLight,
      dark: colors.successDark,
    },
    text: {
      primary: colors.text,
      secondary: colors.textSecondary,
      disabled: colors.textDisabled,
    },
    background: {
      default: colors.background,
      paper: colors.paper,
    },
    action: {
      active: colors.primary,
      hover: colors.primaryDark,
      selected: colors.activeSelected,
      disabled: colors.activeDisabled,
      disabledBackground: colors.activeDisabledBackground,
    },
    divider: colors.border,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        input {
          background-color: ${colors.background};
          color: ${colors.text};
        }
      `,
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.paper,
          color: colors.text,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "initial",
        },
        regular: {
          minHeight: "initial",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          textTransform: "initial",

          "&.cta-button": {
            backgroundColor: colors.secondary,
          },
        },
        sizeLarge: {
          fontSize: "1.4rem",
          padding: "15px 10px 13px",
        },
        sizeMedium: {
          fontSize: "1.2rem",
          lineHeight: "2.1rem",
        },
        sizeSmall: {
          fontSize: "1.1rem",
          minWidth: "35px",
          paddingLeft: "8px",
          paddingRight: "8px",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          borderWidth: "1px",
          color: colors.text,
          fontSize: "1.5rem",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.overwrite": {
            backgroundColor: `${colors.paper}`,
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: colors.border,
            },
            ".MuiInputAdornment-root": {
              backgroundColor: "transparent",
            },
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: "#000",
          backgroundColor: colors.primary,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "1px 1px 6px rgb(0 0 0 / 9%)",
          border: "none",
        },
      },
    },
  },
});
